import React, { useState, useEffect } from 'react';
import {
  Settings,
  Save,
  Database,
  Filter,
  Edit,
  BarChart3
} from 'lucide-react';
import apiService from '../../services/api';
import { formatDate } from '../../utils/dateUtils';
import DataTable, { Table } from '../common/DataTable';
import StatusBadge from '../common/StatusBadge';
import ActionButton from '../common/ActionButton';
import OverviewStats from '../common/OverviewStats';

const SettingsTab = () => {
  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editingKey, setEditingKey] = useState(null);
  const [tempValue, setTempValue] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [showStats, setShowStats] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await apiService.request('/settings');
      setSettings(response);
    } catch (error) {
      console.error('Error loading settings:', error);
      const errorMessage = error.message || error.toString() || 'Unknown error occurred';
      setError(`Failed to load settings: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (setting) => {
    setEditingKey(setting.key);
    // Format initial value based on setting type
    const initialValue = getInitialEditValue(setting);
    setTempValue(initialValue);
    setError('');
    setSuccess('');
  };

  const getInitialEditValue = (setting) => {
    if (Array.isArray(setting.value)) {
      return setting.value.join(', ');
    }
    if (typeof setting.value === 'boolean') {
      return setting.value.toString();
    }
    return String(setting.value || '');
  };

  const handleSave = async (settingKey) => {
    try {
      setSaving(true);
      setError('');
      
      const setting = settings.find(s => s.key === settingKey);
      if (!setting) {
        throw new Error('Setting not found');
      }
      
      let parsedValue = parseSettingValue(setting, tempValue);
      
      await apiService.request(`/settings/${settingKey}`, {
        method: 'PUT',
        body: JSON.stringify({ value: parsedValue })
      });
      
      setSuccess('Setting updated successfully');
      setEditingKey(null);
      loadSettings(); // Reload to get updated data
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
      
    } catch (error) {
      console.error('Error updating setting:', error);
      const errorMessage = error.message || error.toString() || 'Unknown error occurred';
      setError(`Failed to update setting: ${errorMessage}`);
    } finally {
      setSaving(false);
    }
  };

  const parseSettingValue = (setting, value) => {
    // Handle different data types based on setting metadata
    if (setting.data_type === 'array' || Array.isArray(setting.value)) {
      return value.split(',').map(s => s.trim()).filter(s => s.length > 0);
    }
    if (setting.data_type === 'boolean' || typeof setting.value === 'boolean') {
      return value === 'true';
    }
    if (setting.data_type === 'number' || typeof setting.value === 'number') {
      const num = parseFloat(value);
      return isNaN(num) ? value : num;
    }
    return value;
  };

  const handleCancel = () => {
    setEditingKey(null);
    setTempValue('');
    setError('');
  };

  const formatValue = (value) => {
    if (Array.isArray(value)) {
      return value.join(', ');
    }
    return String(value);
  };

  const truncateValue = (value, maxLength = 40) => {
    if (value.length <= maxLength) {
      return value;
    }
    return value.substring(0, maxLength) + '...';
  };

  // Filter settings based on search and category
  const filteredSettings = settings.filter(setting => {
    const matchesSearch = !searchTerm || 
      setting.key.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (setting.description && setting.description.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCategory = !categoryFilter || setting.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  const getSettingIcon = (category) => {
    switch (category) {
      case 'role_analysis':
        return <Database className="w-4 h-4 text-blue-600" />;
      default:
        return <Settings className="w-4 h-4 text-gray-600" />;
    }
  };

  const renderEditInput = (setting) => {
    const inputClass = "w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent";
    
    // Multi-line for arrays or long text
    if (setting.data_type === 'array' || Array.isArray(setting.value)) {
      return (
        <div>
          <textarea
            value={tempValue}
            onChange={(e) => setTempValue(e.target.value)}
            className={inputClass}
            rows="3"
            placeholder="Enter values separated by commas"
          />
          <p className="text-xs text-gray-500 mt-1">
            Enter multiple values separated by commas
          </p>
        </div>
      );
    }
    
    // Boolean select
    if (setting.data_type === 'boolean' || typeof setting.value === 'boolean') {
      return (
        <select
          value={tempValue}
          onChange={(e) => setTempValue(e.target.value)}
          className={inputClass}
        >
          <option value="true">True</option>
          <option value="false">False</option>
        </select>
      );
    }
    
    // Number input
    if (setting.data_type === 'number' || typeof setting.value === 'number') {
      return (
        <input
          type="number"
          value={tempValue}
          onChange={(e) => setTempValue(e.target.value)}
          className={inputClass}
          placeholder="Enter numeric value"
        />
      );
    }
    
    // Default text input
    return (
      <input
        type="text"
        value={tempValue}
        onChange={(e) => setTempValue(e.target.value)}
        className={inputClass}
        placeholder="Enter setting value"
      />
    );
  };

  const columns = [
    {
      header: 'Setting',
      accessor: 'key',
      render: (setting) => (
        <div className="flex items-center space-x-3">
          {getSettingIcon(setting.category)}
          <div>
            <div className="flex items-center space-x-2">
              <span className="font-medium text-gray-900">{setting.key}</span>
              {setting.is_system && (
                <StatusBadge status="System" size="xs" variant="secondary" />
              )}
            </div>
            {setting.description && (
              <p className="text-sm text-gray-600 mt-1">{setting.description}</p>
            )}
          </div>
        </div>
      )
    },
    {
      header: 'Current Value',
      accessor: 'value',
      render: (setting) => (
        editingKey === setting.key ? (
          <div className="space-y-3 min-w-[200px]">
            {renderEditInput(setting)}
            <div className="flex items-center space-x-2">
              <ActionButton
                icon={Save}
                onClick={() => handleSave(setting.key)}
                variant="primary"
                size="sm"
                disabled={saving}
                loading={saving}
              >
                {saving ? 'Saving...' : 'Save'}
              </ActionButton>
              <ActionButton
                onClick={handleCancel}
                variant="secondary"
                size="sm"
                disabled={saving}
              >
                Cancel
              </ActionButton>
            </div>
          </div>
        ) : (
          <div className="max-w-[200px]">
            <div 
              className="bg-gray-50 border border-gray-200 rounded p-2 font-mono text-sm cursor-help hover:bg-gray-100 transition-colors"
              title={formatValue(setting.value)}
            >
              <div className="truncate text-gray-800">
                {truncateValue(formatValue(setting.value))}
              </div>
            </div>
          </div>
        )
      )
    },
    {
      header: 'Type',
      accessor: 'data_type', 
      render: (setting) => {
        const type = setting.data_type || (Array.isArray(setting.value) ? 'array' : typeof setting.value);
        return (
          <StatusBadge 
            status={type} 
            variant={type === 'array' ? 'primary' : type === 'boolean' ? 'success' : 'secondary'} 
            size="xs" 
          />
        );
      }
    },
    {
      header: 'Category',
      accessor: 'category',
      render: (setting) => (
        <span className="text-sm text-gray-600">
          {setting.category || 'General'}
        </span>
      )
    },
    {
      header: 'Last Updated', 
      accessor: 'updated_at',
      render: (setting) => (
        <span className="text-sm text-gray-500">
          {formatDate(setting.updated_at)}
        </span>
      )
    },
    {
      header: 'Actions',
      accessor: 'actions',
      className: 'text-right',
      render: (setting) => (
        editingKey !== setting.key ? (
          <ActionButton
            icon={Edit}
            onClick={() => handleEdit(setting)}
            variant="secondary"
            size="xs"
            tooltip="Edit Setting"
          />
        ) : null
      )
    }
  ];

  return (
    <div className="space-y-6">
      {/* Settings Overview Stats */}
      <OverviewStats 
        show={showStats}
        stats={[
          {
            key: 'total-settings',
            label: 'Total Settings',
            value: settings.length,
            icon: Settings,
            iconColor: 'text-blue-500'
          },
          {
            key: 'role-analysis-settings',
            label: 'Role Analysis',
            value: settings.filter(s => s.category === 'role_analysis').length,
            icon: Database,
            iconColor: 'text-green-500'
          },
          {
            key: 'general-settings',
            label: 'General',
            value: settings.filter(s => s.category === 'general').length,
            icon: Settings,
            iconColor: 'text-purple-500'
          },
          {
            key: 'modified-settings',
            label: 'Recently Modified',
            value: settings.filter(s => {
              const updated = new Date(s.updated_at);
              const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);
              return updated > oneDayAgo;
            }).length,
            icon: Edit,
            iconColor: 'text-orange-500'
          }
        ]}
      />

      <DataTable
        title="System Settings"
        subtitle="Configure application settings and preferences"
        icon={Settings}
        loading={loading}
        error={error}
        success={success}
        onRefresh={loadSettings}
        searchProps={{
          value: searchTerm,
          onChange: (e) => setSearchTerm(e.target.value),
          placeholder: 'Search settings...'
        }}
        filters={[
          {
            type: 'select',
            icon: Filter,
            value: categoryFilter,
            onChange: (e) => setCategoryFilter(e.target.value),
            options: [
              { value: '', label: 'All Categories' },
              { value: 'role_analysis', label: 'Role Analysis' },
              { value: 'general', label: 'General' }
            ]
          }
        ]}
        actions={[
          {
            icon: BarChart3,
            label: showStats ? 'Hide Stats' : 'Show Stats',
            onClick: () => setShowStats(!showStats),
            variant: showStats ? 'primary' : 'secondary'
          }
        ]}
      >
        <Table
          columns={columns}
          data={filteredSettings}
          rowKey="key"
        />
        
        {filteredSettings.length === 0 && (
          <div className="p-12 text-center">
            <Settings className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No settings found</h3>
            <p className="text-gray-600">
              {searchTerm || categoryFilter ? 'No settings match your search criteria.' : 'Settings will appear here once they are configured.'}
            </p>
          </div>
        )}
      </DataTable>
    </div>
  );
};

export default SettingsTab;
